import type React from "react";

import { FormControl, FormControlLabel, FormLabel, Radio, RadioGroup } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import styled from "styled-components";

import { MUIErrorText } from "../inputs/StyledInputs";

export interface Option {
  label: string | React.ReactNode;
  value: string | number;
}

interface Props {
  readonly error?: string;
  readonly label?: string;
  readonly name: string;
  readonly options: Array<Option>;
  readonly required?: boolean;
}

export const MUIRadioGroup: React.FC<Props> = ({ error, label, name, options, required }) => {
  const { control } = useFormContext();

  return (
    <FormControl>
      {label && <StyledFormLabel id={`${name}-label`}>{label}</StyledFormLabel>}
      <Controller
        control={control}
        name={name}
        rules={{ required }}
        render={({ field }) => (
          <StyledRadioGroup
            aria-labelledby={label ? `${name}-label` : undefined}
            value={field.value ?? ""}
            row
            onChange={field.onChange}
            data-testid={`radiogroup-${name}`}
          >
            {options.map(option => (
              <FormControlLabel
                key={option.value}
                value={option.value}
                control={<Radio color="primary" data-testid={`radiogroup-option-${option.value}`} />}
                label={option.label}
              />
            ))}
          </StyledRadioGroup>
        )}
      />

      {!!error && <ErrorText>{error}</ErrorText>}
    </FormControl>
  );
};

const StyledFormLabel = styled(FormLabel)`
  &.MuiFormLabel-root {
    ${props => props.theme.font.link2};
    &.Mui-focused {
      color: ${props => props.theme.colors.redesign.db100};
    }
  }
`;

const StyledRadioGroup = styled(RadioGroup)`
  .MuiFormControlLabel-root {
    display: flex;
    flex-direction: row-reverse;
    border: 1px solid ${props => props.theme.colors.redesign.db30};
    border-radius: 12px;
    padding: 8px 16px;
    margin: 8px 16px 0 0;
  }

  .MuiFormControlLabel-label {
    ${props => ({ ...props.theme.font.link2 })};
    color: ${props => props.theme.colors.redesign.db90} !important;
    user-select: none;
  }

  .MuiRadio-root {
    width: 32px;
    height: 32px;
    margin-left: 8px;
    color: ${props => props.theme.colors.redesign.b30};

    &.MuiRadio-root.Mui-checked {
      color: ${props => props.theme.colors.redesign.b100};
    }
    .MuiSvgIcon-root {
      width: 32px;
      height: 32px;
    }
  }
`;

const ErrorText = styled.div`
  margin-top: 9px;
  ${MUIErrorText}
`;
